export type FeedbackCategory = 'bug' | 'idea' | 'other';

export interface FeedbackRequest {
  readonly category: FeedbackCategory;
  readonly subject: string;
  readonly description: string;
  readonly contact_ok?: boolean;
}

export type FeedbackErrorType =
  | 'unauthorized'
  | 'email_not_verified'
  | 'validation_error'
  | 'rate_limited'
  | 'feature_disabled'
  | 'github_error'
  | 'network_error'
  | 'unknown';

export interface FeedbackError {
  readonly type: FeedbackErrorType;
  readonly message: string;
  readonly retryAfterSeconds?: number;
}

export type FeedbackServerErrorCode =
  | 'unauthorized'
  | 'email_not_verified'
  | 'validation_error'
  | 'rate_limited'
  | 'feature_disabled'
  | 'github_error'
  | 'internal_error';

const SERVER_CODE_TO_ERROR_TYPE: Record<FeedbackServerErrorCode, FeedbackErrorType> = {
  unauthorized: 'unauthorized',
  email_not_verified: 'email_not_verified',
  validation_error: 'validation_error',
  rate_limited: 'rate_limited',
  feature_disabled: 'feature_disabled',
  github_error: 'github_error',
  internal_error: 'unknown',
};

const FEEDBACK_SERVER_ERROR_CODES = new Set<string>(Object.keys(SERVER_CODE_TO_ERROR_TYPE));

export function mapServerError(code: string): FeedbackErrorType {
  if (FEEDBACK_SERVER_ERROR_CODES.has(code)) {
    return SERVER_CODE_TO_ERROR_TYPE[code as FeedbackServerErrorCode];
  }
  return 'unknown';
}

export interface FeedbackErrorBody {
  readonly code: string;
  readonly message?: string;
}

export function isFeedbackErrorBody(body: unknown): body is FeedbackErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as Record<string, unknown>).code === 'string'
  );
}

export function createFeedbackError(
  type: FeedbackErrorType,
  message: string,
  retryAfterSeconds?: number
): FeedbackError {
  return retryAfterSeconds !== undefined ? { type, message, retryAfterSeconds } : { type, message };
}
